import { useState, useCallback, useEffect } from 'react';
import { usePlaidLink } from 'react-plaid-link';
import { Building2, CheckCircle, Loader2, AlertCircle, X } from 'lucide-react';
import { plaidApi } from '../services/api';

interface PlaidLinkProps {
  employeeId: string;
  employeeName: string;
  onSuccess: (data: { bankName: string; accountLast4: string }) => void;
  onClose?: () => void;
  buttonText?: string;
  buttonClassName?: string;
}

export function PlaidLinkButton({
  employeeId,
  employeeName,
  onSuccess,
  onClose,
  buttonText = 'Connect Bank Account',
  buttonClassName = 'btn btn-primary',
}: PlaidLinkProps) {
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch link token on mount
  useEffect(() => {
    const fetchLinkToken = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await plaidApi.createLinkToken(employeeId);
        setLinkToken(response.data.data.linkToken);
      } catch (err: any) {
        setError(err.response?.data?.error?.message || 'Failed to initialize bank connection');
      } finally {
        setLoading(false);
      }
    };

    fetchLinkToken();
  }, [employeeId]);

  const handleSuccess = useCallback(
    async (publicToken: string, metadata: any) => {
      try {
        setLoading(true);
        setError(null);

        // Get the first account selected
        const accountId = metadata.accounts[0]?.id;
        if (!accountId) {
          setError('No account selected');
          return;
        }

        // Exchange token and connect bank account
        const response = await plaidApi.exchangeToken({
          employeeId,
          publicToken,
          accountId,
        });

        const data = response.data.data;
        onSuccess({
          bankName: data.bankName,
          accountLast4: data.accountLast4,
        });
      } catch (err: any) {
        setError(err.response?.data?.error?.message || 'Failed to connect bank account');
      } finally {
        setLoading(false);
      }
    },
    [employeeId, onSuccess]
  );

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess: handleSuccess,
    onExit: (err) => {
      if (err) {
        setError('Bank connection was cancelled');
      }
      onClose?.();
    },
  });

  if (error) {
    return (
      <div className="flex items-center gap-2 text-red-600">
        <AlertCircle className="w-4 h-4" />
        <span className="text-sm">{error}</span>
        <button
          onClick={() => setError(null)}
          className="text-slate-400 hover:text-slate-600"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <button
      onClick={() => open()}
      disabled={!ready || loading}
      className={buttonClassName}
    >
      {loading ? (
        <>
          <Loader2 className="w-4 h-4 animate-spin" />
          Connecting...
        </>
      ) : (
        <>
          <Building2 className="w-4 h-4" />
          {buttonText}
        </>
      )}
    </button>
  );
}

interface BankAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  employeeId: string;
  employeeName: string;
  onSuccess: () => void;
}

export function BankAccountModal({
  isOpen,
  onClose,
  employeeId,
  employeeName,
  onSuccess,
}: BankAccountModalProps) {
  const [step, setStep] = useState<'connect' | 'success'>('connect');
  const [connectedAccount, setConnectedAccount] = useState<{
    bankName: string;
    accountLast4: string;
  } | null>(null);

  if (!isOpen) return null;

  const handleSuccess = (data: { bankName: string; accountLast4: string }) => {
    setConnectedAccount(data);
    setStep('success');
  };

  const handleDone = () => {
    onSuccess();
    onClose();
    setStep('connect');
    setConnectedAccount(null);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold">Connect Bank Account</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {step === 'connect' && (
          <div className="space-y-4">
            <p className="text-slate-600">
              Connect a bank account for <span className="font-semibold">{employeeName}</span> to
              enable direct deposit payouts.
            </p>

            <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-2">How it works</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>1. Click the button below to securely connect</li>
                <li>2. Select your bank and log in</li>
                <li>3. Choose the account for deposits</li>
                <li>4. Done! Payouts will be deposited directly</li>
              </ul>
            </div>

            <div className="bg-slate-50 rounded-lg p-4">
              <div className="flex items-center gap-3 text-sm text-slate-600">
                <img
                  src="https://cdn-icons-png.flaticon.com/128/6124/6124998.png"
                  alt="Plaid"
                  className="w-8 h-8"
                />
                <div>
                  <p className="font-medium text-slate-900">Powered by Plaid</p>
                  <p>Bank-level security. Your credentials are never shared.</p>
                </div>
              </div>
            </div>

            <PlaidLinkButton
              employeeId={employeeId}
              employeeName={employeeName}
              onSuccess={handleSuccess}
              onClose={onClose}
              buttonText="Connect Bank Account"
              buttonClassName="btn btn-primary w-full flex items-center justify-center gap-2"
            />
          </div>
        )}

        {step === 'success' && connectedAccount && (
          <div className="text-center space-y-4">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>

            <div>
              <h3 className="text-lg font-semibold text-slate-900">
                Bank Account Connected!
              </h3>
              <p className="text-slate-600 mt-1">
                {connectedAccount.bankName} ****{connectedAccount.accountLast4}
              </p>
            </div>

            <p className="text-sm text-slate-500">
              {employeeName} will now receive payouts directly to this account.
            </p>

            <button onClick={handleDone} className="btn btn-primary w-full">
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ===========================================
// BANK INVITE MODAL (Manager sends invite to employee)
// ===========================================
interface BankInviteModalProps {
  isOpen: boolean;
  onClose: () => void;
  employeeId: string;
  employeeName: string;
  employeeEmail?: string;
  onSuccess: () => void;
}

export function BankInviteModal({
  isOpen,
  onClose,
  employeeId,
  employeeName,
  employeeEmail,
  onSuccess,
}: BankInviteModalProps) {
  const [email, setEmail] = useState(employeeEmail || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setEmail(employeeEmail || '');
      setError(null);
      setSent(false);
    }
  }, [isOpen, employeeEmail]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      setError('Please enter an email address');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await plaidApi.sendInvite(employeeId, email.trim());

      if (response.data.success) {
        setSent(true);
      } else {
        setError(response.data.error?.message || 'Failed to send invitation');
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to send invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleDone = () => {
    onSuccess();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold">
            {sent ? 'Invitation Sent!' : 'Send Bank Connection Link'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {!sent ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            <p className="text-slate-600">
              Send <span className="font-semibold">{employeeName}</span> a secure link to
              connect their own bank account for direct deposit.
            </p>

            <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-2">Why invite employees?</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Employees securely connect their own accounts</li>
                <li>• You never see their bank credentials</li>
                <li>• They can complete it on their own time</li>
                <li>• Link expires after 7 days for security</li>
              </ul>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1">
                Employee Email
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="employee@email.com"
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                required
              />
            </div>

            {error && (
              <div className="flex items-center gap-2 text-red-600 bg-red-50 p-3 rounded-lg">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 btn btn-primary flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  'Send Invitation'
                )}
              </button>
            </div>
          </form>
        ) : (
          <div className="text-center space-y-4">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>

            <div>
              <h3 className="text-lg font-semibold text-slate-900">
                Invitation Sent
              </h3>
              <p className="text-slate-600 mt-1">
                We've sent a secure link to:
              </p>
              <p className="font-medium text-slate-900 mt-1">{email}</p>
            </div>

            <div className="bg-slate-50 rounded-lg p-4 text-sm text-slate-600 text-left">
              <p className="font-medium text-slate-900 mb-2">What happens next?</p>
              <ul className="space-y-1">
                <li>1. {employeeName} receives the email</li>
                <li>2. They click the link to connect their bank</li>
                <li>3. The bank account appears here once connected</li>
              </ul>
            </div>

            <p className="text-sm text-slate-500">
              The link expires in 7 days. You can resend if needed.
            </p>

            <button onClick={handleDone} className="btn btn-primary w-full">
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Display component for showing connected bank accounts
interface ConnectedAccountProps {
  bankName: string;
  accountLast4: string;
  accountType: string;
  onRemove?: () => void;
  isRemoving?: boolean;
}

export function ConnectedBankAccount({
  bankName,
  accountLast4,
  accountType,
  onRemove,
  isRemoving,
}: ConnectedAccountProps) {
  return (
    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center border">
          <Building2 className="w-5 h-5 text-slate-600" />
        </div>
        <div>
          <p className="font-medium text-slate-900">{bankName}</p>
          <p className="text-sm text-slate-500">
            {accountType} ****{accountLast4}
          </p>
        </div>
      </div>
      {onRemove && (
        <button
          onClick={onRemove}
          disabled={isRemoving}
          className="text-sm text-red-600 hover:text-red-700 disabled:opacity-50"
        >
          {isRemoving ? 'Removing...' : 'Remove'}
        </button>
      )}
    </div>
  );
}
